import { useRef, useState, type DragEvent } from 'react';
import { FileSpreadsheet, Loader2, UploadCloud, X } from 'lucide-react';
import { formatBytes } from '../lib/format';

const MAX_BYTES = 10 * 1024 * 1024;

interface Props {
  onSubmit: (file: File) => void;
  busy: boolean;
}

export function UploadArea({ onSubmit, busy }: Props): JSX.Element {
  const inputRef = useRef<HTMLInputElement>(null);
  const [file, setFile] = useState<File | null>(null);
  const [localError, setLocalError] = useState<string | null>(null);
  const [dragging, setDragging] = useState(false);

  function validate(candidate: File): string | null {
    if (!/\.xlsx$/i.test(candidate.name)) {
      return 'Formato inválido. Envie a planilha de Stakeholders no formato .xlsx.';
    }
    if (candidate.size > MAX_BYTES) {
      return `Arquivo muito grande (${formatBytes(candidate.size)}). O limite é de 10 MB.`;
    }
    if (candidate.size === 0) {
      return 'O arquivo está vazio.';
    }
    return null;
  }

  function accept(candidate: File | undefined): void {
    if (!candidate) return;
    const error = validate(candidate);
    if (error) {
      setFile(null);
      setLocalError(error);
      return;
    }
    setLocalError(null);
    setFile(candidate);
  }

  function handleDrop(event: DragEvent<HTMLDivElement>): void {
    event.preventDefault();
    setDragging(false);
    accept(event.dataTransfer.files?.[0]);
  }

  return (
    <div className="space-y-4">
      <div
        onDragOver={(event) => {
          event.preventDefault();
          setDragging(true);
        }}
        onDragLeave={() => setDragging(false)}
        onDrop={handleDrop}
        className={[
          'rounded-xl2 border-2 border-dashed p-10 text-center transition',
          dragging ? 'border-brand-red bg-brand-red/5' : 'border-brand-line bg-white',
        ].join(' ')}
      >
        <input
          ref={inputRef}
          type="file"
          accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
          className="sr-only"
          onChange={(event) => accept(event.target.files?.[0])}
        />
        <UploadCloud className="mx-auto h-12 w-12 text-brand-slate" aria-hidden />
        <p className="mt-4 text-base font-semibold text-brand-ink">Arraste a planilha de Stakeholders aqui</p>
        <p className="mt-1 text-sm text-brand-slate">Somente arquivos .xlsx, até 10 MB. A planilha original não é alterada.</p>
        <button type="button" className="btn-secondary mt-6" onClick={() => inputRef.current?.click()} disabled={busy}>
          Selecionar arquivo
        </button>
      </div>

      {file && (
        <div className="card flex items-center justify-between gap-4 p-4">
          <div className="flex min-w-0 items-center gap-3">
            <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-state-positive/10">
              <FileSpreadsheet className="h-5 w-5 text-state-positive" aria-hidden />
            </span>
            <div className="min-w-0">
              <p className="truncate text-sm font-semibold text-brand-ink">{file.name}</p>
              <p className="text-xs text-brand-slate">{formatBytes(file.size)}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              className="btn-ghost px-3"
              onClick={() => setFile(null)}
              disabled={busy}
              aria-label="Remover arquivo"
            >
              <X className="h-4 w-4" aria-hidden />
            </button>
            <button type="button" className="btn-primary" onClick={() => onSubmit(file)} disabled={busy}>
              {busy && <Loader2 className="h-4 w-4 animate-spin" aria-hidden />}
              {busy ? 'Lendo planilha…' : 'Ler e conferir dados'}
            </button>
          </div>
        </div>
      )}

      {localError && (
        <p role="alert" className="rounded-xl border border-brand-red/30 bg-brand-red/5 px-4 py-3 text-sm text-brand-redDark">
          {localError}
        </p>
      )}
    </div>
  );
}
